import {
  AssignExperimentPayloadSchema,
  AssignExperimentResolveSchema,
} from "@/lib/toolCardSchemas/assignExperiment";
import {
  ApproveExperimentPayloadSchema,
  ApproveExperimentResolveSchema,
} from "@/lib/toolCardSchemas/approveExperiment";
import {
  SetupTrackerPayloadSchema,
  SetupTrackerResolveSchema,
} from "@/lib/toolCardSchemas/setupTracker";
import {
  LockTrackerPayloadSchema,
  LockTrackerResolveSchema,
} from "@/lib/toolCardSchemas/lockTracker";
import {
  MarkCompletePayloadSchema,
  MarkCompleteResolveSchema,
} from "@/lib/toolCardSchemas/markComplete";
import {
  InsightsDraftPayloadSchema,
  InsightsDraftResolveSchema,
} from "@/lib/toolCardSchemas/insightsDraft";
import {
  LearningGatePayloadSchema,
  LearningGateResolveSchema,
} from "@/lib/toolCardSchemas/learningGate";
import {
  LearningCardPayloadSchema,
  LearningCardResolveSchema,
} from "@/lib/toolCardSchemas/learningCard";
import {
  LearningQaPayloadSchema,
  LearningQaResolveSchema,
} from "@/lib/toolCardSchemas/learningQa";
import {
  SourcesPayloadSchema,
  SourcesResolveSchema,
} from "@/lib/toolCardSchemas/sources";
import {
  ConnectSourcePayloadSchema,
  ConnectSourceResolveSchema,
} from "@/lib/toolCardSchemas/connectSource";
import {
  AssumptionSelectionResolveSchema,
  EntitySelectionArgsSchema,
  ExperimentCardSelectionResolveSchema,
  HypothesisSelectionResolveSchema,
  QuestionAnswerSubmissionSchema,
} from "@/lib/toolCardSchemas/entitySelection";

/**
 * Renderer and client-schema registry for server-authored Action Requests.
 * - wireToolName: assistant-ui tool-call toolName
 * - resolveSchema: validates action-specific submission fields
 * - payloadSchema: validates the server-authored card payload in the UI
 *
 * Human tool UI pattern: show success only when `result` is set (isHumanToolCallResolved),
 * not when message status is complete. The request id correlates assistant and workflow surfaces.
 */
export const toolCardRegistry = {
  assumption_selection: {
    kind: "assumption_selection",
    wireToolName: "assumption_selection",
    payloadSchema: EntitySelectionArgsSchema,
    resolveSchema: AssumptionSelectionResolveSchema.omit({ toolCallId: true }),
    shell: "assumption-selection",
  },
  question_answer: {
    kind: "question_answer",
    wireToolName: "question_answer",
    payloadSchema: EntitySelectionArgsSchema,
    resolveSchema: QuestionAnswerSubmissionSchema,
    shell: "clarifying-questions",
  },
  hypothesis_selection: {
    kind: "hypothesis_selection",
    wireToolName: "hypothesis_selection",
    payloadSchema: EntitySelectionArgsSchema,
    resolveSchema: HypothesisSelectionResolveSchema.omit({ toolCallId: true }),
    shell: "hypothesis-selection",
  },
  experiment_card_selection: {
    kind: "experiment_card_selection",
    wireToolName: "experiment_card_selection",
    payloadSchema: EntitySelectionArgsSchema,
    resolveSchema: ExperimentCardSelectionResolveSchema.omit({ toolCallId: true }),
    shell: "experiment-selection",
  },
  sources: {
    kind: "sources",
    wireToolName: "sources",
    payloadSchema: SourcesPayloadSchema,
    resolveSchema: SourcesResolveSchema,
    shell: "sources-attach",
  },
  assign_experiment: {
    kind: "assign_experiment",
    wireToolName: "assign_experiment",
    payloadSchema: AssignExperimentPayloadSchema,
    resolveSchema: AssignExperimentResolveSchema,
    shell: "assign-experiment",
  },
  approve_experiment: {
    kind: "approve_experiment",
    wireToolName: "approve_experiment",
    payloadSchema: ApproveExperimentPayloadSchema,
    resolveSchema: ApproveExperimentResolveSchema,
    shell: "gate-confirm",
  },
  setup_tracker: {
    kind: "setup_tracker",
    wireToolName: "setup_tracker",
    payloadSchema: SetupTrackerPayloadSchema,
    resolveSchema: SetupTrackerResolveSchema,
    shell: "tracker-setup",
  },
  lock_tracker: {
    kind: "lock_tracker",
    wireToolName: "lock_tracker",
    payloadSchema: LockTrackerPayloadSchema,
    resolveSchema: LockTrackerResolveSchema,
    shell: "gate-confirm",
  },
  mark_complete: {
    kind: "mark_complete",
    wireToolName: "mark_complete",
    payloadSchema: MarkCompletePayloadSchema,
    resolveSchema: MarkCompleteResolveSchema,
    shell: "gate-confirm",
  },
  insights_draft: {
    kind: "insights_draft",
    wireToolName: "insights_draft",
    payloadSchema: InsightsDraftPayloadSchema,
    resolveSchema: InsightsDraftResolveSchema,
    shell: "insights-editor",
  },
  learning_gate: {
    kind: "learning_gate",
    wireToolName: "learning_gate",
    payloadSchema: LearningGatePayloadSchema,
    resolveSchema: LearningGateResolveSchema,
    shell: "gate-binary",
  },
  learning_qa: {
    kind: "learning_qa",
    wireToolName: "learning_qa",
    payloadSchema: LearningQaPayloadSchema,
    resolveSchema: LearningQaResolveSchema,
    shell: "qa-form",
  },
  learning_card: {
    kind: "learning_card",
    wireToolName: "learning_card",
    payloadSchema: LearningCardPayloadSchema,
    resolveSchema: LearningCardResolveSchema,
    shell: "summary-card",
  },
  connect_source: {
    kind: "connect_source",
    wireToolName: "connect_source",
    payloadSchema: ConnectSourcePayloadSchema,
    resolveSchema: ConnectSourceResolveSchema,
    shell: "connect-source",
  },
} as const;

export type ToolCardRegistryKind = keyof typeof toolCardRegistry;
